/**
 * The "AI calls out" form fields: which numbers the AI rings and rings from,
 * and the limits every AI call runs inside.
 *
 * The draft and the conversion live in `aiOutboundNotes.ts`; this file only
 * draws them. The automation switch, the calling window and the gap between
 * calls are `AiOutboundAutomationFields`; the retention is stated on the card,
 * never edited.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import { OUTBOUND_MAX_TEST_NUMBERS } from "../../../../../lib/aiOutbound";
import { formatPhone } from "../../../../../lib/phone";
import { Field, FieldBox, FieldGroup, Note, Pill, SelectPill, ToggleRow } from "../../panel/fields";
import { OUTBOUND_LIMITS, fromNumberOptions, readTestNumbers, type OutboundDraft } from "./aiOutboundNotes";

export function AiOutboundFields({
  draft,
  onChange,
  savedFromNumber,
  mainNumber,
  ownedNumbers,
}: {
  draft: OutboundDraft;
  onChange: (next: OutboundDraft) => void;
  /** The calling number the server last answered with, kept in the menu when Twilio does not list it. */
  savedFromNumber: string;
  mainNumber: string;
  ownedNumbers: string[] | null;
}) {
  const t = useT();

  function set<K extends keyof OutboundDraft>(key: K, value: OutboundDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  const numbers = readTestNumbers(draft.testNumbers);
  const options = fromNumberOptions(savedFromNumber, ownedNumbers, mainNumber);

  function optionLabel(value: string, kind: "main" | "owned" | "saved"): string {
    if (kind === "main") {
      return mainNumber
        ? t("settings.aiOutbound.fromNumberMain", { number: formatPhone(mainNumber) })
        : t("settings.aiOutbound.fromNumberMainUnset");
    }
    if (kind === "saved") return t("settings.aiOutbound.fromNumberSaved", { number: formatPhone(value) });
    return formatPhone(value);
  }

  return (
    <>
      <FieldGroup title={t("settings.aiOutbound.numbersGroup")}>
        <Field
          label={t("settings.aiOutbound.testNumbers")}
          hint={t("settings.aiOutbound.testNumbersHint", { max: OUTBOUND_MAX_TEST_NUMBERS })}
        >
          {/* `crm-area`, the same box the answering tab's test callers use:
              `crm-inp` is a fixed flex row whose child rules reach only input
              and select. */}
          <textarea
            className="crm-area min-h-[86px] font-mono text-[12.5px]"
            rows={3}
            value={draft.testNumbers}
            placeholder="+37255512345"
            spellCheck={false}
            autoComplete="off"
            onChange={(e) => set("testNumbers", e.target.value)}
          />
        </Field>
        {/* Said before saving: a line that is not a number is dropped, and a
            sixth number would refuse the whole save. */}
        {numbers.invalid.length > 0 ? (
          <Note tone="warn">{t("settings.aiOutbound.testNumbersInvalid", { lines: numbers.invalid.join(", ") })}</Note>
        ) : null}
        {numbers.overflow.length > 0 ? (
          <Note tone="warn">
            {t("settings.aiOutbound.testNumbersOverflow", {
              max: OUTBOUND_MAX_TEST_NUMBERS,
              lines: numbers.overflow.map(formatPhone).join(", "),
            })}
          </Note>
        ) : null}

        {/* `FieldBox`, not `Field`: the menu is a button, and a label would
            forward its own clicks to it. */}
        <FieldBox label={t("settings.aiOutbound.fromNumber")} hint={t("settings.aiOutbound.fromNumberHint")}>
          <SelectPill
            value={draft.fromNumber}
            onChange={(next) => set("fromNumber", next)}
            label={t("settings.aiOutbound.fromNumber")}
          >
            {options.map((o) => (
              <option key={o.value || "main"} value={o.value}>
                {optionLabel(o.value, o.kind)}
              </option>
            ))}
          </SelectPill>
        </FieldBox>
        {ownedNumbers === null ? <Note tone="mut">{t("settings.aiOutbound.ownedUnavailable")}</Note> : null}
      </FieldGroup>

      <FieldGroup title={t("settings.aiOutbound.limitsGroup")}>
        {/* Three or four digits each, so each wears a pill the width of the
            answer, as the answering tab's timings do. */}
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-3">
          {OUTBOUND_LIMITS.map(({ key, bounds }) => (
            <Field
              key={key}
              label={t(`settings.aiOutbound.${key}`)}
              hint={t(`settings.aiOutbound.${key}Hint`, { min: bounds.min, max: bounds.max, fallback: bounds.fallback })}
            >
              <Pill
                className="h-10 max-w-[120px]"
                value={draft[key]}
                onChange={(next) => set(key, next)}
                inputMode="numeric"
                placeholder={String(bounds.fallback)}
              />
            </Field>
          ))}
        </div>
        <ToggleRow
          label={t("settings.aiOutbound.machineDetection")}
          hint={t("settings.aiOutbound.machineDetectionHint")}
          checked={draft.machineDetection}
          onChange={(machineDetection) => set("machineDetection", machineDetection)}
        />
      </FieldGroup>
    </>
  );
}
